<script setup lang="ts">
import { reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router';
import Registration from '../../sdk/models/Registration';
import Course from '../../sdk/models/Product';
import EntityApi from '../../sdk/EntityApi';
import Checkbox from '../controls/Checkbox.vue';

const route = useRoute();
const router = useRouter();
const productApi = new EntityApi('products');
const registrationApi = new EntityApi('registrations');
class FormValidation {
    NameValid: boolean|null = null;
    EmailValid: boolean|null = null;
    MobileValid: boolean|null = null;
    PaidValid: boolean|null = null;
}
const data = reactive({ 
    registration: new Registration(),
    product: null as Course|null,
    formValidatione: new FormValidation()
})

const rowKey = route.params.rowKey as string;
data.product = await productApi.retrieve(rowKey);
data.registration.PartitionKey = data.product?.PartitionKey + "^" + data.product?.RowKey + "^" + data.product?.StartDate;

const validateName = function() {
    data.formValidatione.NameValid = data.registration.Name != null && data.registration.Name.length > 0;
}

const validateEmail = function() {
    data.formValidatione.EmailValid = data.registration.Email != null && /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/.test(data.registration.Email);
}

const mobileDisplay = () => data.product?.MobileNumberDisplay ?? 'Nicht anzeigen';
const showMobile = () => mobileDisplay() !== 'Nicht anzeigen';
const mobileRequired = () => mobileDisplay() === 'Anzeigen (pflicht)';

const paidDisplay = () => data.product?.PaidDisplay ?? 'Nicht anzeigen';
const showPaid = () => paidDisplay() !== 'Nicht anzeigen';
const paidRequired = () => paidDisplay() === 'Anzeigen (pflicht)';

const validateMobile = function() {
    if(!showMobile()) {
        data.formValidatione.MobileValid = null;
        return;
    }
    if(mobileRequired()) {
        data.formValidatione.MobileValid = !!(data.registration.MobileNumber && data.registration.MobileNumber.trim().length > 0);
    } else {
        // optional: valid if empty or has some digits
        const value = data.registration.MobileNumber?.trim();
        data.formValidatione.MobileValid = value == null || value.length === 0 || /[0-9+()\-\s]{5,}/.test(value);
    }
}

const validatePaid = function() {
    if(!showPaid()) {
        data.formValidatione.PaidValid = null;
        return;
    }
    if(paidRequired()) {
        data.formValidatione.PaidValid = data.registration.Paid === true;
    } else {
        // optional: always valid
        data.formValidatione.PaidValid = true;
    }
}

const registerCourse = async function(e:any) {
    validateName();
    validateEmail();
    validateMobile();
    validatePaid();

    if(data.formValidatione.NameValid === true && data.formValidatione.EmailValid === true && (data.formValidatione.MobileValid !== false) && (data.formValidatione.PaidValid !== false)) {
        const result = await registrationApi.create(data.registration);
        if(result.ok) {
            router.push("/kurse/registrationsbestaetigung");
        } else {
            router.push("/error");
        }
    }
    
    e.preventDefault();
}

</script>

<template>
    <div class="registration-form">
        <h3>Kurs-Registration</h3>
        <div class="registration-form-body row">
            <div class="col-xs-12 col-md-7 mobile-order-late">
                <div class="box-row item-style cardshadow">
                    <h3 class="form-body-title">Angaben des/der Teilnehmenden</h3>

                    <label for="senderName" class="label-style">Name</label>
                    <input type="text" name="senderName" id="senderName" class="textbox-style" autocomplete="name" v-model="data.registration.Name">
                    <label v-if="data.formValidatione.NameValid === false" for="senderName" class="label-style label-warning">Bitte Name eingeben.</label>

                    <label for="email" class="label-style">Email</label>
                    <input type="text" name="email" id="email" class="textbox-style" autocomplete="email" v-model="data.registration.Email">
                    <label v-if="data.formValidatione.EmailValid === false" for="email" class="label-style label-warning">Bitte eine korrekte Emailadresse eingeben.</label>

                    <label for="comment" class="label-style">Kommentar (optional)</label>
                    <textarea type="text" rows="4" name="comment" id="comment" class="textarea-style" v-model="data.registration.Comment"></textarea>

                    <div v-if="showMobile()">
                        <label for="mobileNumber" class="label-style">Mobile-Nummer <span v-if="mobileRequired()">(pflicht)</span></label>
                        <input type="text" name="mobileNumber" id="mobileNumber" class="textbox-style" autocomplete="tel" v-model="data.registration.MobileNumber" @blur="validateMobile">
                        <label v-if="data.formValidatione.MobileValid === false && mobileRequired()" for="mobileNumber" class="label-style label-warning">Bitte geben Sie Ihre Mobile-Nummer ein.</label>
                    </div>

                    <div v-if="showPaid()">
                        <Checkbox c-id="paid" v-model="data.registration.Paid" :text="paidRequired() ? 'Bezahlt (pflicht)' : 'Bezahlt'" />
                        <label v-if="data.formValidatione.PaidValid === false && paidRequired()" for="paid" class="label-style label-warning">Bitte bestätigen Sie die Bezahlung.</label>
                    </div>

                    <Checkbox c-id="allowemails" v-model="data.registration.AllowEmails" text="Ich möchte gerne Emails zu neuen Kursen und Produkten erhalten." />

                    <button type="submit" name="submit" class="buttonstyle" style="margin-top:3rem; margin-bottom: 1rem;" @click="registerCourse">
                        <span class="">Absenden</span>
                    </button>
                </div>
            </div>
            <div class="col-xs-12 col-md-5 mobile-order-early">
                <div class="box-row item-style cardshadow">
                    <h3 class="form-body-title">Kurs-Informationen</h3>
                    <div class="course-description-row">
                        <div class="course-description-label">Name</div>
                        <div>{{ data.product?.Name }}</div>
                    </div>
                    <div class="course-description-row">
                        <div class="course-description-label">Beschreibung</div>
                        <div style="white-space: pre-wrap;">{{ data.product?.Description}}</div>
                    </div>
                    <div class="course-description-row">
                        <div class="course-description-label">Start</div>
                        <div>{{ data.product?.StartDate }}</div>
                    </div>
                    <div v-if="data.product?.EndDate" class="course-description-row">
                        <div class="course-description-label">Ende</div>
                        <div>{{ data.product?.EndDate }}</div>
                    </div>
                    <div v-if="data.product?.Price" class="course-description-row">
                        <div class="course-description-label">Preis</div>
                        <div>{{ data.product?.Price }} CHF/EUR</div>
                    </div>
                </div>
            </div>
        </div>
        
        
    </div>


</template>

<style scoped>

    .course-description-row {
        display:flex;
        line-height: 4rem;
    }
    .course-description-label {
        width:14rem;
    }

    div[class^="col-"] {
        margin-bottom: 1rem;
    }

    .form-body-title {
        padding-top: 2rem;
        padding-bottom: 1rem;
    }

    .label-style {
        font-weight: 400;
        line-height: 1.5;
        font-family: Roboto,sans-serif;
        color: #323335;
        margin: 0;
        padding: 0;
        padding-top: 1rem;
        display: block;
        margin-bottom: 10px;
        font-size: 18px;
    }

    .textbox-style {
        box-sizing: border-box;
        width: 100%;
        padding: 15px;
        border: 1px solid #c6c6c6;
        border-radius: 5px;
        color: #323335;
        font-size: 16px;
        font-family: inherit;
        line-height: 1.25;
        background-color: #fff;
        box-shadow: none;
        height: 50px;
    }

    @media (max-width: 991px) {
        .registration-form-body {
            display: flex;
            flex-direction: column;
        }

        .mobile-order-early {
            order: -1;
        }

        .mobile-order-late {
            order: 1;
        }
    }

    

</style>
